import Image from 'components/Common/Image'
import FareFamilyClassLabel from 'components/Flights/V2/FareFamily/FareFamilyClassLabel'
import FareFamilyFlightTileV2Baggage from 'components/Flights/V2/FareFamily/FareFamilyFlightTileV2Baggage'
import FareFamilyFlightTileV2TimeLine from 'components/Flights/V2/FareFamily/FareFamilyFlightTileV2TimeLine'
import Group from 'components/utils/Group'
import { INTL_DMY_CASUAL_FORMAT, INTL_SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { rem } from 'polished'
import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getFareFamilyBaggageInclusion } from 'lib/flights/flightTileBaggage'
import { cartPanelTripCartLegPaddingStyles } from './cartPanelLayout.styles'

const Container = styled(Group)`
  ${cartPanelTripCartLegPaddingStyles}
`

const PrimaryContent = styled.div`
  flex-grow: 1;
`

const AirlineLogoContainer = styled.div`
  width: ${rem(120)};
  height: ${rem(52)};
`

const AirlineLogoImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const BaggageGroup = styled(Group)`
  min-width: ${rem(220)};
`

interface Props {
  flight: App.Checkout.FlightItemFlight
  isClassicReward?: boolean
}

function CartPanelFlightLegExtension({ flight, isClassicReward }: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const baggageInfo = useMemo(() => {
    const fareFamilyBaggage = getFareFamilyBaggageInclusion(flight.fareFamily)
    return {
      carryOnIncluded: fareFamilyBaggage.carryOn ?? flight.hasIncludedCarryOnBaggage,
      checkedBaggageIncluded: fareFamilyBaggage.checked ?? flight.hasIncludedCheckedBaggage,
    }
  }, [flight.fareFamily, flight.hasIncludedCarryOnBaggage, flight.hasIncludedCheckedBaggage])

  const formattedTimes = useMemo(() => ({
    departureTime: flight.departureDateTime
      ? dateFormatter(flight.departureDateTime, INTL_SHORT_TIME_FORMAT_24_HOUR)
      : undefined,
    arrivalTime: flight.arrivalDateTime
      ? dateFormatter(flight.arrivalDateTime, INTL_SHORT_TIME_FORMAT_24_HOUR)
      : undefined,
    departureDate: dateFormatter(flight.departureDateTime, INTL_DMY_CASUAL_FORMAT),
    arrivalDate: dateFormatter(flight.arrivalDateTime, INTL_DMY_CASUAL_FORMAT),
    duration: flight.duration ? intl.formatMessage(
      {
        id: 'bGkrZgVl6IGhP-q1n1I7',
        defaultMessage: '{hours}h {minutes}m',
        description: 'Flight duration in hours and minutes for the cart review flight tile',
      },
      { hours: flight.duration.hours, minutes: flight.duration.minutes },
    ) : undefined,
  }), [dateFormatter, flight.arrivalDateTime, flight.departureDateTime, flight.duration, intl])

  return (
    <Container direction="vertical" tabletDirection="horizontal" gap={16} desktopGap={32} tabletWrap="wrap">
      <PrimaryContent>
        <Group direction="horizontal" gap={16} desktopGap={48} verticalAlign="center">
          <AirlineLogoContainer>
            <AirlineLogoImage
              width={40}
              image={flight.carrierImage}
            />
          </AirlineLogoContainer>

          <FareFamilyFlightTileV2TimeLine
            departureTime={formattedTimes.departureTime}
            arrivalTime={formattedTimes.arrivalTime}
            arrivalDate={formattedTimes.arrivalDate}
            departureDate={formattedTimes.departureDate}
            departureAirport={flight.departingAirportCode}
            arrivalAiport={flight.arrivalAirportCode}
            duration={formattedTimes.duration}
            stops={flight.numberOfStops || 0}
          />
        </Group>
      </PrimaryContent>

      <BaggageGroup direction="horizontal-reverse" desktopDirection="horizontal" gap={8} desktopGap={32} horizontalAlign="start" verticalAlign="center">
        <Group direction="horizontal" desktopDirection="vertical" gap={8} verticalAlign="center">
          <FareFamilyFlightTileV2Baggage
            carryOnIncluded={baggageInfo.carryOnIncluded}
            checkedBaggageIncluded={baggageInfo.checkedBaggageIncluded}
          />
        </Group>
        {flight.fareFamily && (
          <FareFamilyClassLabel fareFamily={flight.fareFamily} isClassicReward={isClassicReward} />
        )}
      </BaggageGroup>
    </Container>
  )
}

export default memo(CartPanelFlightLegExtension)
